import {apolloClient, gql} from '../graphql';
import {logAndThrowGraphQLErrors, normalizeGraphQLObject} from '../graphql/helpers';
import type {Customer} from 'shared/src/models';

export const getCustomers = async (): Promise<Customer[]> => {
	const {data, error} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetCustomers {
				customers {
					id
					name
					email
					phoneNumber
					notes
					address
				}
			}
		`),
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	return data.customers.map((customer) => ({
		...normalizeGraphQLObject(customer),
	}));
};

export const getCustomer = async (customerId: string): Promise<Customer> => {
	const customer = (await getCustomers()).find((customer) => {
		return customer.id === customerId;
	});

	if (!customer) {
		throw new Error('Customer not found');
	}

	return customer;
};

export const createCustomer = async (newCustomer: Omit<Customer, 'id'>): Promise<Customer> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation CreateCustomer($customer: NewCustomerInput!) {
				createCustomer(customer: $customer)
			}
		`),
		variables: {
			customer: {
				...newCustomer,
			},
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		...newCustomer,
		id: data!.createCustomer,
	};
};

export const updateCustomer = async ({
	id: customerId,
	...restCustomer
}: Customer): Promise<Customer> => {
	const {errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation UpdateCustomer($customerId: ID!, $customer: NewCustomerInput!) {
				updateCustomer(customerId: $customerId, customer: $customer)
			}
		`),
		variables: {
			customerId,
			customer: {
				...restCustomer,
			},
		},
	});

	if (errors && errors.length > 0) {
		logAndThrowGraphQLErrors(errors);
	}

	return {
		id: customerId,
		...restCustomer,
	};
};
